<template>
    <v-flex
        v-if="display"
        class="xs12 px-12 py-6"
        :style="{backgroundColor: bgColor}"
        tabindex="0"
        id="coupon-communication-details"
    >
        <v-divider aria-hidden="true" class="my-4" />
        <span class="coupon-label">
            {{ $t('coupon.additionalInformation') }}
        </span>
        <div>
            <ul style="list-style: disc inside;" class="pl-2 pt-2">
                <coupon-confirm-email :appointment="appointment" />
                <coupon-reminder-email v-if="!remindIsWithin24h" :appointment="appointment" :confirmation="confirmation" />
                <coupon-reminder-sms v-if="!remindIsWithin24h" :appointment="appointment" :confirmation="confirmation" />
                <coupon-virtual-reminder v-if="isVirtualService" :appointment="appointment" />
                <coupon-next-appt-book-reminder v-if="hasNextApptBookReminder" :appointment="appointment" />
            </ul>
        </div>
    </v-flex>
</template>

<script>
import { mapGetters } from 'vuex';
import { shadeColor } from '@/utils';
import { find } from 'lodash';
import { differenceInHours } from 'date-fns';
import { ConfigurationMessageEnum, ServiceSettings } from '@/enums';
import CouponConfirmEmail from '@/components/coupon/communications/CouponConfirmEmail';
import CouponNextApptBookReminder from '@/components/coupon/communications/CouponNextApptBookReminder';
import CouponReminderEmail from '@/components/coupon/communications/CouponReminderEmail';
import CouponReminderSms from '@/components/coupon/communications/CouponReminderSms';
import CouponVirtualReminder from '@/components/coupon/communications/CouponVirtualReminder';

export default {
    name: 'coupon-communication-details',
    components: {
        CouponConfirmEmail,
        CouponNextApptBookReminder,
        CouponReminderEmail,
        CouponReminderSms,
        CouponVirtualReminder,
    },
    props: {
        appointment: { type: Object, required: true },
        isVirtualService: { type: Boolean, required: false, default: false },
    },
    computed: {
        ...mapGetters({
            upperLocale: 'upperLocale',
            getSendByEmail: 'metadataForm/getSendByEmail',
            getCommunications: 'establishment/getCommunications',
            getCurrentEstSettings: 'services/getCurrentEstSettings',
            getSelectedServiceConfigurationMessageById: 'services/getSelectedServiceConfigurationMessageById',
        }),

        /**
         * Returns a shaded color based on the primary theme color
         */
        bgColor() {
            return shadeColor(this.$vuetify.theme.themes.light.primary, 0.92);
        },

        /**
         * Returns true if we must display the service coupon message
         */
        display() {
            return !!((this.getSendByEmail && this.hasClientData && this.appointment.clientData.email) ||
                this.showReminderEmailInformation ||
                this.showReminderSMSInformation ||
                this.isVirtualService ||
                this.hasNextApptBookReminder);
        },

        /**
         * Returns true if there are client data associated with the appointment
         */
        hasClientData() {
            return this.appointment && this.appointment.clientData;
        },

        /**
         * Returns true if the appointment is in the next 24 hours
         */
        remindIsWithin24h() {
            let dateNow = new Date();
            let dateStartAppointment = new Date(this.appointment.start);
            let hours = differenceInHours(dateStartAppointment, dateNow);
            return (hours < 24);
        },

        /**
         * Returns true if we must display the SMS information for the reminder
         */
        showReminderSMSInformation() {
            return this.confirmation &&
                this.confirmation.sendSms &&
                this.hasClientData &&
                this.appointment.clientData.cellphone &&
                !this.remindIsWithin24h;
        },

        /**
         * Returns true if we must display the email information for the reminder
         */
        showReminderEmailInformation() {
            return this.confirmation &&
                this.confirmation.sendEmail &&
                this.hasClientData &&
                this.appointment.clientData.email &&
                !this.remindIsWithin24h;
        },

        /**
         * Return the appointment-config-message Object for the establishment
         */
        confirmation() {
            return find(this.getCommunications, {
                configurationMessage: ConfigurationMessageEnum.APPOINTMENT_CONFIRMATION_REQUEST,
            });
        },

        /**
         * Return true if the next-appt-booking-reminder is enable for this service and we have an email or an cellphone
         */
        hasNextApptBookReminder() {
            if (!this.hasClientData || (!this.appointment.clientData.email && !this.appointment.clientData.cellphone)) {
                return false;
            }

            const setting = find(this.getCurrentEstSettings, {
                setting: ServiceSettings.ENABLE_NEXT_SERVICE_TO_BOOK_FOLLOW_UP,
            });
            return !!setting && setting.value;
        },
    },
};
</script>

<style lang="scss" scoped>
    .coupon-label {
        display: block;
        padding-bottom: 10px;
    }
</style>
